<template>
  <OnboardingLayout
    :is-next-allowed="!!(answers.name && answers.size && answers.industry)"
    :is-next-loading="isNextLoading"
    :is-prev-button-shown="true"
    @next-step="$emit('nextStep')"
    @prev-step="$emit('prevStep')"
  >
    <template #left>
      <div class="title">
        <OrganizationIcon class="title-icon" />
        <h1>Tell us about your organization.</h1>
      </div>

      <section>
        <h2>What is its name?</h2>
        <FormInput
          ref="orgNameInput"
          v-model:value="orgName"
          type="text"
          placeholder="Organization name…"
        />
      </section>

      <section>
        <h2>How many people work there?</h2>
        <div class="blocks">
          <div
            v-for="(content, key) in values.teamSize"
            :key="key"
            class="block sm"
            :class="{ isSelected: answers.size === key }"
            @click="$emit('answers', 'size', key)"
          >
            <p>{{ content }}</p>
          </div>
        </div>
      </section>

      <section>
        <h2>Which industry best describes it?</h2>
        <div class="blocks">
          <div
            v-for="(content, key) in values.industries"
            :key="key"
            class="block sm"
            :class="{ isSelected: answers.industry === key }"
            @click="$emit('answers', 'industry', key)"
          >
            <p>{{ content }}</p>
          </div>
        </div>
      </section>

      <p v-if="error" class="error">
        {{ error }}
      </p>
    </template>

    <template #right>
      <img :src="getImageUrl(imgName)" />
    </template>
  </OnboardingLayout>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
} from "vue";

import FormInput from "@/components/graph/definition/form/FormInput.vue";
import OrganizationIcon from "@/components/icons/OrganizationIcon.vue";
import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import { metrics } from "@/metrics";

export default defineComponent({
  components: {
    FormInput,
    OnboardingLayout,
    OrganizationIcon,
  },
  props: {
    answers: {
      type: Object as PropType<{
        name: string | undefined;
        size: string | undefined;
        industry: string | undefined;
      }>,
      required: true,
    },
    isNextLoading: { type: Boolean, required: false, default: false },
    error: { type: String, required: false },
  },
  emits: {
    answers: (_question: string | undefined, _answer: string | undefined) =>
      true,
    nextStep: () => true,
    prevStep: () => true,
  },
  setup(props, { emit }) {
    const orgNameInput = ref<InstanceType<typeof FormInput> | null>(null);
    const imgName = ref<string>("step_org");
    const values = computed(() => ({
      teamSize: {
        "2-30": "2-30",
        "30-100": "30-100",
        "100-300": "100-300",
        "300-1k": "300-1K",
        "1k+": "1k+",
      },
      industries: {
        software: "Software",
        agency: "Agency",
        finance: "Finance",
        healthcare: "Healthcare",
        education: "Education",
        ecommerce: "E-commerce",
        manufacturing: "Manufacturing",
        nonprofit: "Non-profit",
        other: "Other",
      },
    }));

    const orgName = computed<string>({
      get: () => props.answers.name ?? "",
      set: (v) => emit("answers", "name", v.trim() === "" ? undefined : v),
    });

    metrics.segment.page("Onboarding", { step: "organization" });

    onMounted(async () => {
      await nextTick();
      window.requestAnimationFrame(() => {
        orgNameInput.value?.focus?.();
      });
    });

    const getImageUrl = (name: string) => {
      return new URL(`../../assets/onboarding/${name}.png`, import.meta.url)
        .href;
    };

    return {
      orgNameInput,
      imgName,
      values,
      getImageUrl,
      orgName,
    };
  },
});
</script>

<style scoped lang="scss">
.error {
  @extend %font-caption-regular;
  margin-top: 12px;
  @include themed() {
    color: t($secondary);
  }
}

.title {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  column-gap: 10px;
  margin-bottom: 6px;

  :deep(h1) {
    margin: 0;
  }
}

.title-icon {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  position: relative;
  top: 3px;
}
</style>
